import { useCallback, useState } from 'react'
import { DateTime } from 'luxon'

import {
  uploadDocument,
  UploadUserDocumentPayload,
  userUploadDocument,
  UploadError,
} from '@/actions/awsActions'
import { UploadDocumentType } from '@/constants/businessConstants'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { parseErrorFromCatch } from '@/utils/errorHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getCurrentUser } from '@/selectors/user.selectors'
import { selectUserDocumentCategoryByInternalName } from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { useAnalyticsTrack } from '@/features/amplitude'

import { IUploadedDocument } from '../lib/types'
import { StatementData } from '../StatementUploadSection'

export interface IAdditionalTrackingProperties {
  document_subcategory?: string
  document_name?: string
  tax_year?: number
  [key: string]: string | number | undefined
}

export interface IUseDocumentUploadOptions {
  additionalTrackingProperties?: IAdditionalTrackingProperties
  documentCategory?: string
  documentType: UploadDocumentType
  year?: string
  month?: number
  extraPayload?: Record<string, string | number | (string | number)[]>
  statementData?: StatementData[]
  taxDocumentType?: 'federal' | 'state'
  userDocumentCategoryId?: number | null
  onUploadSuccess?: (
    uploadedDocuments: IUploadedDocument[]
  ) => Promise<void> | void
  onUploadError?: (error: string) => void
}
export interface IUseDocumentUploadReturn {
  uploadDocuments: (documents: File[]) => Promise<void>
  isUploading: boolean
  error: string
  clearError: () => void
}

export const useDocumentUpload = ({
  additionalTrackingProperties,
  documentCategory,
  documentType,
  year,
  month,
  extraPayload,
  statementData = [],
  taxDocumentType,
  userDocumentCategoryId: providedUserDocumentCategoryId,
  onUploadSuccess,
  onUploadError,
}: IUseDocumentUploadOptions): IUseDocumentUploadReturn => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const user = useReselector(getCurrentUser)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState('')
  const isAdmin = (user && 'admin' in user ? user.admin : false) || false

  const clearError = useCallback(() => {
    setError('')
  }, [])

  const userDocumentCategory = useReselector(
    selectUserDocumentCategoryByInternalName,
    documentCategory
  )
  const userDocumentCategoryId =
    providedUserDocumentCategoryId ?? (userDocumentCategory?.id || null)

  const uploadSingleDocument = useCallback(
    async (
      document: File,
      index: number
    ): Promise<IUploadedDocument | null> => {
      if (!user?.id) {
        throw new Error('User is required for document upload')
      }

      let docRes: UserDocument | UploadError | null = null

      const statementMonth =
        year !== undefined && month !== undefined
          ? DateTime.fromObject({ year: Number(year), month }, { zone: 'utc' })
          : undefined

      // Extract file-specific metadata from extraPayload
      const fileMonth = extraPayload?.[`${document.name}_month`]
      const fileAccountId = extraPayload?.[`${document.name}_accountId`]

      // BankStatementUploadList writes fileMonth in MONTH_YEAR ('MMM YYYY')
      let fileStatementMonth: DateTime | undefined = statementMonth
      if (fileMonth && typeof fileMonth === 'string') {
        const parsed = DateTime.fromFormat(
          fileMonth,
          DATE_FORMATS_LUXON.MONTH_YEAR,
          { zone: 'utc' }
        )
        if (parsed.isValid) {
          fileStatementMonth = parsed
        }
      }

      const payload: UploadUserDocumentPayload = {
        file: document,
        user: {
          id: user.id,
          uuid: user.uuid,
        },
        documentType,
        statementMonth: fileStatementMonth,
        userDocumentCategoryId,
        year:
          documentType === UploadDocumentType.TAX && year ? year : undefined,
      }

      // Add transactionId from extraPayload if provided
      if (
        extraPayload?.transactionId &&
        typeof extraPayload.transactionId === 'number'
      ) {
        payload.transactionId = extraPayload.transactionId
      }

      // Add statement data - prioritize file-specific metadata, then statementData array
      const shouldUseFileMetadata = fileMonth || fileAccountId
      if (shouldUseFileMetadata) {
        // Handle both single values and arrays for financialAccountIds
        let financialAccountIds: number[] = []
        if (fileAccountId) {
          if (Array.isArray(fileAccountId)) {
            financialAccountIds = fileAccountId.filter(
              (id): id is number => typeof id === 'number'
            )
          } else if (typeof fileAccountId === 'number') {
            financialAccountIds = [fileAccountId]
          }
        }

        const statement = {
          statementMonth:
            fileStatementMonth?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH) ?? '',
          financialAccountIds,
        }

        payload.statement = statement
      } else if (statementData[index]) {
        const statement = {
          statementMonth: statementData[index].statementMonth,
          financialAccountIds: statementData[index].financialAccountIds,
        }

        payload.statement = statement
      }

      // Choose upload function based on user type
      if (isAdmin) {
        docRes = await uploadDocument(payload)(dispatch)
      } else {
        const res = await userUploadDocument(payload)(dispatch)

        if (res && 'userDocument' in res) {
          docRes = res.userDocument
        } else if (res && 'error' in res) {
          docRes = res
        }
      }

      // Handle upload errors
      if (docRes && 'error' in docRes) {
        throw new Error(docRes.error)
      }

      // Transform successful upload to IUploadedDocument
      if (docRes && 'id' in docRes) {
        return {
          title: docRes.fileDescription || '',
          url: docRes.signedUrl || '',
          id: docRes.id,
          statementMonth,
          taxDocumentType,
          fileName: document.name,
          doc: docRes,
        }
      }

      return null
    },
    [
      user?.id,
      user?.uuid,
      isAdmin,
      year,
      month,
      documentType,
      userDocumentCategoryId,
      extraPayload,
      dispatch,
      taxDocumentType,
      statementData,
    ]
  )

  const uploadDocuments = useCallback(
    async (documents: File[]) => {
      if (!user?.id) {
        const errorMessage = 'User is required for document upload'
        setError(errorMessage)
        onUploadError?.(errorMessage)
        return
      }

      if (documents.length === 0) {
        const errorMessage = 'No documents selected for upload'
        setError(errorMessage)
        onUploadError?.(errorMessage)
        return
      }

      setError('')
      setIsUploading(true)

      try {
        // Upload all documents in parallel
        const results = await Promise.all(
          documents.map((document, index) =>
            uploadSingleDocument(document, index)
          )
        )

        // Filter out null results
        const successfulUploads = results.filter(
          (r): r is IUploadedDocument => r !== null
        )

        const uploadedDocuments: IUploadedDocument[] = successfulUploads.map(
          (result) => ({
            ...result,
          })
        )

        // Track upload analytics
        track('document upload completed', {
          document_category: documentType,
          document_count: uploadedDocuments.length,
          upload_type: isAdmin ? 'admin' : 'user',
          ...additionalTrackingProperties,
        })

        // Call success callback
        if (onUploadSuccess) {
          await onUploadSuccess(uploadedDocuments)
        }
      } catch (err) {
        const errorMessage = parseErrorFromCatch(err)
        setError(errorMessage)
        onUploadError?.(errorMessage)

        // Track upload error
        track('document upload failed', {
          document_category: documentType,
          error: errorMessage,
          upload_type: isAdmin ? 'admin' : 'user',
          ...additionalTrackingProperties,
        })
      } finally {
        setIsUploading(false)
      }
    },
    [
      additionalTrackingProperties,
      documentType,
      isAdmin,
      user?.id,
      track,
      onUploadSuccess,
      onUploadError,
      uploadSingleDocument,
    ]
  )

  return {
    uploadDocuments,
    isUploading,
    error,
    clearError,
  }
}
